import React, { useMemo } from 'react';
import { getAvatarUrl } from '../utils/avatarUtils';
import { environment } from '../config/environment';

interface LeaderboardCardProps {
  player: {
    id: string;
    name: string;
    score: number;
  };
  rank: number;
  variant?: 'leaderboard' | 'podium';
}

/**
 * Memoized leaderboard card component for displaying player rankings
 * Supports both leaderboard (INTERMISSION) and podium (PODIUM) variants
 * Only re-renders when player data or rank actually changes
 */
const LeaderboardCard: React.FC<LeaderboardCardProps> = ({
  player,
  rank,
  variant = 'leaderboard'
}) => {
  // Memoize avatar URL calculation
  const avatarUrl = useMemo(
    () => getAvatarUrl(player.name, variant === 'podium' ? 64 : 48, environment.api.avatar),
    [player.name, variant]
  );

  // Podium variant with medals and gradients
  if (variant === 'podium') {
    const medalEmoji = rank === 1 ? '🥇' : rank === 2 ? '🥈' : '🥉';
    const gradientClass =
      rank === 1 ? 'bg-gradient-to-r from-yellow-100 to-yellow-200 border-2 border-yellow-400' :
      rank === 2 ? 'bg-gradient-to-r from-gray-100 to-gray-200 border-2 border-gray-400' :
      'bg-gradient-to-r from-orange-100 to-orange-200 border-2 border-orange-400';

    return (
      <div className={`flex justify-between items-center rounded-lg p-6 ${gradientClass}`}>
        <div className="flex items-center">
          <span className="text-2xl mr-3">{medalEmoji}</span>
          <img
            src={avatarUrl}
            alt={`${player.name}'s avatar`}
            className="w-12 h-12 rounded-full border-2 border-yellow-500 mr-3"
          />
          <span className="font-bold text-lg text-gray-900">{player.name}</span>
        </div>
        <span className="font-bold text-xl text-gray-900">{player.score}</span>
      </div>
    );
  }

  // Leaderboard variant (default)
  return (
    <div className="flex items-center bg-gray-100 rounded-lg p-4">
      <span className="font-bold text-gray-900 mr-3">#{rank}</span>
      <img
        src={avatarUrl}
        alt={`${player.name}'s avatar`}
        className="w-10 h-10 rounded-full border-2 border-gray-300 mr-3"
      />
      <span className="font-medium text-gray-700 flex-1">{player.name}</span>
      <span className="font-bold text-gray-900">{player.score}</span>
    </div>
  );
};

// Memoize the component to prevent re-renders when props haven't changed
export default React.memo(LeaderboardCard);
